import React, { useEffect, useState } from 'react'
import { collection, getDocs, limit, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../firebase-config'

const DEFAULT_PERMISSIONS = {
  canApproveNormative: false,
  canManageCoins: false,
  canAccessSupport: false,
}

export default function UserManagement() {
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [userData, setUserData] = useState(null)
  const [toast, setToast] = useState(null)
  const [coinsDelta, setCoinsDelta] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [usersDirectory, setUsersDirectory] = useState([])
  const [directoryLoading, setDirectoryLoading] = useState(false)

  const showToast = ({ message, description, type = 'success' }) => {
    setToast({ message, description, type })
    setTimeout(() => setToast(null), 3500)
  }

  const pickCanonicalUserDoc = (docs = []) => {
    if (!Array.isArray(docs) || !docs.length) return null
    const scored = docs.map((docSnap) => {
      const data = docSnap.data() || {}
      const uid = String(data.uid || '').trim()
      const id = String(docSnap.id || '').trim()
      const isAdmin = data.admin === true || String(data.role || '') === 'admin'
      const updatedAt = Number(data?.updatedAt?.toMillis?.() || 0)
      const lastLogin = Date.parse(String(data.last_login || '')) || 0
      const active = data.active !== false
      const isLegacy = String(data.merge_status || '').toLowerCase() === 'legacy' || Boolean(data.merged_into)
      const score = (uid && uid === id ? 100 : 0) + (isAdmin ? 10 : 0) + (uid ? 5 : 0) + (active ? 3 : 0) + (isLegacy ? -50 : 0)
      return { docSnap, data, score, ts: Math.max(updatedAt, lastLogin) }
    })
    scored.sort((a, b) => (b.score - a.score) || (b.ts - a.ts))
    return scored[0]
  }

  const loadUserByIdentifier = async (rawIdentifier) => {
    const raw = String(rawIdentifier || '').trim()
    if (!raw) return null
    const callGetUserAdminSnapshot = httpsCallable(functions, 'getUserAdminSnapshot')
    const resp = await callGetUserAdminSnapshot({ targetUser: raw })
    const payload = resp?.data || {}
    const user = payload?.user || null
    if (!user) return null
    const wallet = payload?.wallet || {}
    return {
      ...user,
      role: user.role || (user.admin ? 'admin' : 'user'),
      permissions: { ...DEFAULT_PERMISSIONS, ...(user.permissions || {}) },
      coins_balance: Number(wallet.coins_balance || 0),
      coins_expire_at: wallet.coins_expire_at || null,
      wallet_id: wallet.id || null,
    }
  }

  const searchUser = async () => {
    setError('')
    setSuccess('')
    setUserData(null)

    const raw = String(searchInput || '').trim()
    if (!raw) {
      setError('Ingrese email o UID para buscar.')
      return
    }

    setLoading(true)
    try {
      const loaded = await loadUserByIdentifier(raw)
      if (!loaded) return setError('Usuario no registrado.')
      setUserData(loaded)
    } catch (e) {
      setError('Error buscando usuario. Intente nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const raw = String(searchInput || '').trim().toLowerCase()
    if (raw.length < 3) {
      setSuggestions([])
      return
    }
    let active = true
    const timer = setTimeout(async () => {
      try {
        setSuggestionsLoading(true)
        let snap = await getDocs(query(collection(db, 'users'), where('email', '>=', raw), where('email', '<=', `${raw}\uf8ff`), limit(8)))
        if (snap.empty) {
          snap = await getDocs(query(collection(db, 'users'), where('uid', '>=', raw), where('uid', '<=', `${raw}\uf8ff`), limit(8)))
        }
        if (!active) return
        const dedupByUid = new Map()
        snap.docs.forEach((d) => {
          const row = { id: d.id, ...(d.data() || {}) }
          const key = String(row.uid || row.email || d.id)
          if (!dedupByUid.has(key)) dedupByUid.set(key, [])
          dedupByUid.get(key).push(d)
        })
        const clean = Array.from(dedupByUid.values())
          .map((docs) => pickCanonicalUserDoc(docs))
          .filter(Boolean)
          .map((picked) => ({ id: picked.docSnap.id, ...(picked.data || {}) }))
        setSuggestions(clean)
      } catch {
        if (active) setSuggestions([])
      } finally {
        if (active) setSuggestionsLoading(false)
      }
    }, 250)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [searchInput])

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        setDirectoryLoading(true)
        const snap = await getDocs(query(collection(db, 'users'), limit(1000)))
        if (!active) return
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((u) => u.active !== false && String(u.merge_status || '').toLowerCase() !== 'legacy' && !u.merged_into)
          .sort((a, b) => String(a.email || a.uid || a.id).localeCompare(String(b.email || b.uid || b.id), 'es', { sensitivity: 'base' }))
        setUsersDirectory(rows)
      } catch {
        if (active) setUsersDirectory([])
      } finally {
        if (active) setDirectoryLoading(false)
      }
    })()
    return () => { active = false }
  }, [])

  const roleBadgeClass = (role) => {
    if (role === 'admin') return 'border-rose-200 bg-rose-50 text-rose-700'
    if (role === 'support') return 'border-amber-200 bg-amber-50 text-amber-700'
    return 'border-slate-200 bg-slate-100 text-slate-700'
  }

  const saveUserRole = async () => {
    if (!userData) return
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const callUpdateUserPermissions = httpsCallable(functions, 'updateUserPermissions')
      await callUpdateUserPermissions({
        targetUser: userData.uid || userData.email,
        role: userData.role,
        permissions: userData.permissions,
      })

      setSuccess('Cambios guardados y auditados correctamente.')
      showToast({
        message: `Permisos actualizados para ${userData.email || userData.uid}`,
        description: 'El cambio impacto en el motor y quedo registrado en auditoria.',
        type: 'success',
      })
    } catch (e) {
      setError('No se pudieron guardar los cambios. Verifique permisos admin.')
      showToast({
        message: 'Error de autorizacion',
        description: e?.message || 'No tienes permisos suficientes para esta operacion.',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const adjustCoins = async () => {
    if (!userData) return
    const delta = Number(coinsDelta || 0)
    if (!Number.isFinite(delta) || delta === 0) {
      setError('Ingresa una cantidad de coins valida (puede ser positiva o negativa).')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')
    try {
      const callAdjustUserCoins = httpsCallable(functions, 'adjustUserCoins')
      const resp = await callAdjustUserCoins({
        targetUser: userData.uid || userData.email,
        deltaCoins: delta,
        reason: 'admin_panel_manual_adjust',
      })
      const newBalance = Number(resp?.data?.newBalance || 0)
      setUserData((prev) => ({ ...prev, coins_balance: newBalance }))
      setCoinsDelta('')
      setSuccess('Coins actualizados correctamente.')
      showToast({
        message: 'Coins actualizados',
        description: `Nuevo saldo: ${newBalance}`,
        type: 'success',
      })
    } catch (e) {
      setError(e?.message || 'No se pudo actualizar el saldo de coins.')
      showToast({
        message: 'Error al cargar coins',
        description: e?.message || 'Operacion rechazada.',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded-xl bg-white p-4 shadow space-y-4">
      {toast && (
        <div className={`rounded-md border px-3 py-2 text-sm ${toast.type === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          <p className="font-semibold">{toast.message}</p>
          <p className="text-xs opacity-90">{toast.description}</p>
        </div>
      )}
      <h3 className="text-lg font-bold text-slate-800">IAM - Gestion de Usuarios</h3>

      <div className="flex gap-2">
        <input
          type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por email o UID"
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={searchUser}
          disabled={loading}
          className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:bg-slate-400"
        >
          Buscar
        </button>
      </div>

      <div className="rounded border border-slate-200 bg-slate-50 p-2">
        <p className="mb-2 text-xs font-semibold text-slate-700">Usuarios registrados (orden alfabético)</p>
        {directoryLoading ? (
          <p className="text-xs text-slate-500">Cargando usuarios...</p>
        ) : (
          <div className="max-h-56 overflow-y-auto rounded border border-slate-200 bg-white">
            {(usersDirectory.filter((u) => {
              const q = String(searchInput || '').trim().toLowerCase()
              if (!q) return true
              const key = `${String(u.email || '')} ${String(u.uid || '')} ${String(u.id || '')}`.toLowerCase()
              return key.includes(q)
            })).map((u) => {
              const role = u.role || u.rol || (u.admin ? 'admin' : 'user')
              const label = String(u.email || u.uid || u.id)
              return (
                <button
                  key={u.uid || u.id}
                  type="button"
                  onClick={async () => {
                    try {
                      const uid = String(u.uid || u.id || '')
                      setSearchInput(uid || label)
                      setError('')
                      setSuccess('')
                      const loaded = await loadUserByIdentifier(uid || label)
                      if (loaded) setUserData(loaded)
                      else setError('Usuario no registrado.')
                    } catch {
                      setError('No se pudo cargar el saldo de coins del usuario.')
                    }
                  }}
                  className="flex w-full items-center justify-between border-b border-slate-100 px-2 py-1.5 text-left text-xs hover:bg-slate-100"
                >
                  <span className="truncate pr-2">{label}</span>
                  <span className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${roleBadgeClass(role)}`}>{role}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {(suggestionsLoading || suggestions.length > 0) && (
        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
          {suggestionsLoading && <p className="text-slate-500">Buscando coincidencias...</p>}
          {!suggestionsLoading && suggestions.map((u) => {
            const label = u.email || u.uid || u.id
            return (
              <button
                key={u.uid || u.id}
                type="button"
                onClick={async () => {
                  try {
                    setSearchInput(label)
                    const loaded = await loadUserByIdentifier(label)
                    if (loaded) {
                      setUserData(loaded)
                      setError('')
                    } else {
                      setError('Usuario no registrado.')
                    }
                  } catch {
                    setError('No se pudo cargar el saldo de coins del usuario.')
                  }
                  setSuggestions([])
                }}
                className="block w-full rounded px-2 py-1 text-left hover:bg-slate-200"
              >
                {label}
              </button>
            )
          })}
        </div>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}
      {success && <p className="text-sm text-green-700">{success}</p>}

      {userData && (
        <div className="space-y-4 rounded border border-slate-200 p-4">
          <div className="grid gap-2 text-sm md:grid-cols-2">
            <div><strong>Nombre:</strong> {userData.nombre || '-'} {userData.apellido || ''}</div>
            <div><strong>Email:</strong> {userData.email || '-'}</div>
            <div><strong>UID:</strong> {userData.uid || '-'}</div>
            <div><strong>Coins:</strong> {Number(userData.coins_balance || 0)}</div>
            <div><strong>Rol actual:</strong> {userData.role}</div>
            <div><strong>Registro:</strong> {String(userData.createdAt || userData.fechaAlta || '-')}</div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Rol</label>
              <select
                value={userData.role}
                onChange={(e) => setUserData((prev) => ({ ...prev, role: e.target.value }))}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="user">Usuario</option>
                <option value="support">Soporte</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Permisos</label>
              <div className="space-y-1 text-sm">
                {Object.keys(DEFAULT_PERMISSIONS).map((key) => (
                  <label key={key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(userData.permissions?.[key])}
                      onChange={(e) => setUserData((prev) => ({
                        ...prev,
                        permissions: {
                          ...(prev.permissions || {}),
                          [key]: e.target.checked,
                        },
                      }))}
                    />
                    <span>{key}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveUserRole}
              disabled={loading}
              className="rounded bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800 disabled:bg-cyan-400"
            >
              Guardar cambios
            </button>
          </div>

          <div className="rounded border border-slate-200 p-3">
            <p className="mb-2 text-xs font-semibold text-slate-700">Cargar / descontar coins</p>
            <div className="flex gap-2">
              <input
                type="number"
                value={coinsDelta}
                onChange={(e) => setCoinsDelta(e.target.value)}
                placeholder="Ej: 10 o -5"
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={adjustCoins}
                disabled={loading}
                className="rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:bg-emerald-400"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
